import {
  HierarchyAndStudentNumbers,
  HierarchyAndStudentNumbersTransfer,
  NodeAndOverride,
  RawHierarchyAndStudentNumbers,
} from '../common';

/**
 * Converts actual hierarchy-and-student-numbers data into the initial state for the
 * modal. This ONLY handles the initialisation of the UI from the API state; it is
 * not responsible for normalising the output of using the UI, nor is it responsible
 * for any UI behaviour beyond initialisation.
 *
 * This is a pretty weird thing to do, but it is necessary to achieve UX parity with RL.
 *
 * The extraction will do the following:
 *
 * [1] IF we have neither nodes nor list-attached student-numbers, return unchanged
 * [2] IF we only have list-attached student-numbers, return unchanged
 * [3] IF we only have nodes, return unchanged
 * [4] IF we have both nodes and list-attached student-numbers, we also:
 * [4a] IF any node has student-number overrides, clear the list-attached numbers
 * [4b] IF no node has no student-number overrides, transfer the list-attached numbers to the first node in the list, then clear the list-attached numbers.
 *
 * @param  {RawHierarchyAndStudentNumbers} rawHierarchyAndStudentNumbers hierarchy data from API
 * @return {HierarchyAndStudentNumbers}                                  hierarchy data to initialise UI
 */
export function extractInitialHierarchyAndStudentNumbersForUI(
  rawHierarchyAndStudentNumbers: RawHierarchyAndStudentNumbers,
): HierarchyAndStudentNumbers {
  const { listStudentNumbers, nodesAndOverrides } =
    rawHierarchyAndStudentNumbers;

  if (Array.isArray(nodesAndOverrides) && nodesAndOverrides.length > 0) {
    // [3] IF we only have nodes, return unchanged
    if (!Number.isInteger(listStudentNumbers)) {
      return {
        listStudentNumbers: null,
        nodesAndOverrides,
        initialStatus: HierarchyAndStudentNumbersTransfer.NoTransfer,
      };
    }

    // [4a] IF any node has student-number overrides, clear the list-attached numbers
    if (
      nodesAndOverrides.some(node =>
        Number.isInteger(node.overriddenStudentNumbers),
      )
    ) {
      return {
        listStudentNumbers: null,
        nodesAndOverrides,
        initialStatus: HierarchyAndStudentNumbersTransfer.NoTransfer,
      };
    }
    // [4b] IF no node has no student-number overrides, transfer the list-attached numbers to the first node in the list, then clear the list-attached numbers.
    const [firstNode, ...restOfNodes] = nodesAndOverrides;
    const firstNodeWithListNumbers: NodeAndOverride = {
      ...firstNode,
      overriddenStudentNumbers: listStudentNumbers,
    };
    const restOfNodesWithZeroes = restOfNodes.map(({ node }) => ({
      node,
      overriddenStudentNumbers: 0,
    }));
    return {
      listStudentNumbers: null,
      nodesAndOverrides: [firstNodeWithListNumbers, ...restOfNodesWithZeroes],
      initialStatus:
        HierarchyAndStudentNumbersTransfer.FromLegacyToFirstExistingNode,
    };
  } else if (Number.isInteger(listStudentNumbers)) {
    // [2] IF we only have list-attached student-numbers, change initialStatus
    return {
      listStudentNumbers,
      nodesAndOverrides: null,
      initialStatus:
        HierarchyAndStudentNumbersTransfer.FromListToFirstNewlyAddedNode,
    };
  }

  // [1] IF we have neither nodes nor list-attached student-numbers, return unchanged
  return {
    listStudentNumbers: null,
    nodesAndOverrides: null,
    initialStatus: HierarchyAndStudentNumbersTransfer.NoTransfer,
  };
}
